import { memo } from 'react';
import styled from '@emotion/styled';
import {
  borderRadius,
  fontSize,
  fontWeight,
  mediaMax,
  spacing,
  zIndex,
} from 'presentations/views/styles';
import { black, white } from 'presentations/views/styles/color';
import {
  AllDayTimeGridView,
  CalendarView,
  WeekdayHeaderView,
} from 'presentations/views/components/atoms/Calendar';
import { useBookingCalendar } from './hooks';
import {
  EventListItem,
  MergeableEventListItem,
  Schedule,
} from '../../atoms/Calendar/reducer';
import DayTimeGridList from 'presentations/views/components/atoms/Calendar/DayTimeGridList';
import { css } from '@emotion/react';
import Icon from 'presentations/views/components/atoms/Icon';
import Link from 'presentations/views/components/atoms/Link';
import { helpPath } from 'presentations/routes/path';
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

export interface Props {
  onClickEvent: (event: MergeableEventListItem, element: HTMLElement) => void;
  onClickBookingSlot?: (event: EventListItem) => void;
  schedules: Schedule[] | null;
  isLoading?: boolean;
  isInvalid?: boolean;
  color: string;
}

const Component = memo((props: Props) => {
  const { actions } = useBookingCalendar({
    schedules: props.schedules,
    isLoading: props.isLoading,
    onClickBookingSlot: props.onClickBookingSlot,
  });

  const t = translateInComponent(translations);

  return (
    <DayTimeContainer>
      <Header>
        <WeekdayHeaderView />
        <AllDayTimeGridView onClickEvent={props.onClickEvent} />
      </Header>
      {props.isInvalid ? (
        <InvalidContainer
          href={helpPath('7155141#h_0c0c14ca6d')}
          selectedColor={props.color}
        >
          <InvalidDescription>{t('Invalid Description')}</InvalidDescription>
          <InvalidLabelContainer>
            <InvalidStyledIcon
              selectedColor={props.color}
              name="chevron-right"
            />
            <InvalidLabel>{t('Invalid Help')}</InvalidLabel>
          </InvalidLabelContainer>
        </InvalidContainer>
      ) : (
        <DayTimeGridList
          onClickEvent={props.onClickEvent}
          onClickBookingSlot={actions.onClickBookingSlot}
          isLoading={props.isLoading}
        />
      )}
    </DayTimeContainer>
  );
});
const Header = styled.div`
  position: sticky;
  z-index: ${zIndex(1100)};
  top: 0;
  left: 0;
  background-color: ${white};
`;

const DayTimeContainer = styled(CalendarView)`
  background-color: ${white};
  width: 100%;
  height: inherit;
  ${mediaMax.tablet} {
    width: 100vw;
    min-width: 640px;
  }
`;
const CalendarUnavailableButtonLayout = styled.div`
  width: 300px;
  top: 120px;
  left: calc(50% - 150px);
`;
const InvalidDescription = styled.div`
  color: ${black};
  font-size: ${fontSize(13)};
  margin-bottom: ${spacing(8)};
`;
const InvalidLabel = styled.div`
  font-size: ${fontSize(16)};
  font-weight: ${fontWeight('bold')};
  color: ${black};
`;

const InvalidContainer = styled(Link)<{
  selectedColor?: string;
}>`
  width: 320px;
  margin: ${spacing(32)} auto;
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ selectedColor }) => css`
    ${selectedColor || ''};
  `}
  border-radius: ${borderRadius(6)};
  padding: ${spacing(16)} ${spacing(40)};
  box-shadow: 0px 0px 16px rgba(0, 0, 0, 0.15);
  background-color: white;
  align-items: center;
  color: ${black};
  :hover, :hover ${InvalidLabel} {
    ${({ selectedColor }) => css`
      color: ${selectedColor || ''};
    `}
  }
`;

const InvalidLabelContainer = styled.div`
  display: flex;
  align-items: center;
`;
const InvalidStyledIcon = styled(Icon)<{
  selectedColor?: string;
}>`
  width: 24px !important;
  height: 24px !important;
  ${({ selectedColor }) => css`
    color: ${selectedColor || ''};
  `}
`;
export default Component;
